import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import { ArtData } from '@/database/art'
import { Context } from '@/components/Context'
import CheckBox from '@/components/boxes/CheckBox'
import ListingOverview from '@/components/auction/ListingOverview'
import DetailsOverview from '@/components/auction/DetailsOverview'
import { ArtState } from '@/database/types'
import { UpdateType } from '@/types/websocket'

const tabName = '#auction'

const MemoizedCheckBox = React.memo(CheckBox)
const MemoizedListingOverview = React.memo(ListingOverview)
const MemoizedDetailsOverview = React.memo(DetailsOverview)

const AuctionOverview = (): ReactElement => {
    const { auctionDataList, currentAccount, setActiveUpdates, filters, toggleFilter } = useContext(Context)
    const [isModalAuctionCardDetailsOpen, setIsModalAuctionCardDetailsOpen] = useState(false)
    const [modalArtData, setModalArtData] = useState<ArtData>({} as ArtData)
    const { forced, auctionMyClaims, auctionMyBids, auctionMyArt } = filters

    const openModalAuctionCardDetails = useCallback((artData: ArtData) => {
        if (artData) {
            setModalArtData(artData)
            setIsModalAuctionCardDetailsOpen(true)
        }
    }, [])

    const closeModal = useCallback(() => {
        window.location.hash = tabName
        setIsModalAuctionCardDetailsOpen(false)
    }, [])

    useEffect(() => {
        const checkHashAndOpenModal = () => {
            const hash = window.location.hash
            const match = hash.match(/#nft-(\d+)/)
            if (match) {
                const selectedArt: ArtData | undefined = auctionDataList.find(
                    (artData) => artData.tokenId === Number(match[1]),
                )
                if (
                    selectedArt &&
                    (selectedArt.state === ArtState.AUCTION || selectedArt.state === ArtState.FORCED_AUCTION)
                ) {
                    openModalAuctionCardDetails(selectedArt)
                }
            } else {
                window.location.hash = tabName
            }
        }

        // Check hash on mount
        checkHashAndOpenModal()

        // Add event listener for hash changes
        window.addEventListener('hashchange', checkHashAndOpenModal, false)

        // Cleanup event listener on component unmount
        return () => window.removeEventListener('hashchange', checkHashAndOpenModal, false)
    }, [auctionDataList, currentAccount])

    useEffect(() => {
        setActiveUpdates([UpdateType.AUCTIONS, UpdateType.PORTFOLIO])
    }, [])

    return (
        <div>
            <h1 className="title">Timed Auctions</h1>
            <div className="container">
                <p className={'padding-bottom'}>
                    {!isModalAuctionCardDetailsOpen && (
                        <MemoizedCheckBox
                            btnName="Forced"
                            classStyles=""
                            checked={forced}
                            handleClick={() => toggleFilter('forced')}
                        />
                    )}
                    {!isModalAuctionCardDetailsOpen && (
                        <MemoizedCheckBox
                            btnName="My&nbsp;Art"
                            classStyles=""
                            checked={auctionMyArt}
                            handleClick={() => toggleFilter('auctionMyArt')}
                        />
                    )}
                    {!isModalAuctionCardDetailsOpen && (
                        <MemoizedCheckBox
                            btnName="My&nbsp;Bids"
                            classStyles=""
                            checked={auctionMyBids}
                            handleClick={() => toggleFilter('auctionMyBids')}
                        />
                    )}
                    {!isModalAuctionCardDetailsOpen && (
                        <MemoizedCheckBox
                            btnName="My&nbsp;Claims"
                            classStyles=""
                            checked={auctionMyClaims}
                            handleClick={() => toggleFilter('auctionMyClaims')}
                        />
                    )}
                </p>
                <div className="prompts">
                    <ul>
                        {!isModalAuctionCardDetailsOpen && (
                            <MemoizedListingOverview
                                openModalAuctionCardDetails={openModalAuctionCardDetails}
                                myArt={auctionMyArt}
                                myBids={auctionMyBids}
                                myClaims={auctionMyClaims}
                                forced={forced}
                            />
                        )}
                        {isModalAuctionCardDetailsOpen && modalArtData && (
                            <MemoizedDetailsOverview artData={modalArtData} onClose={closeModal} />
                        )}
                    </ul>
                </div>
            </div>
        </div>
    )
}

export default React.memo(AuctionOverview)
